import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { ReactComponent as IconDownload } from "../../../../shared/assets/Icons/download-solid.svg";
import { useAppSelector } from "@redux/hooks";
import { RequireTenantType } from "@shared/components/RequireTenantType";
import { useRequireTenantType } from "@shared/hooks/useRequireTenantType";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import {
  Report,
  getUndownloadableReason,
  isReportDownloadable,
  reportsBaseColumns,
} from "./ReportsData";
import { makeReportFields } from "./reportsFieldSpec";
import {
  fetchReportDownload,
  fetchReportsList,
  readApiErrorMessage,
  requestReportGeneration,
  type GenerateReportBody,
} from "./request/request";
import GenerateReportModal from "./GenerateReportModal";
import { useReportsAutoRefresh } from "./useReportsAutoRefresh";

/** The page is a partner/admin surface; customers never reach it. */
const ALLOWED_TENANT_TYPES = ["PARTNER", "GLOBAL_ADMIN"] as const;

export default function ReportsPage() {
  // ========== Access Control ==========
  // Check access status to prevent unnecessary API calls
  const { isAllowed } = useRequireTenantType([...ALLOWED_TENANT_TYPES]);

  const api = useAxiosAuth0();
  const { isAuthenticated, isLoading } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);

  // ========= Table data =========
  const [reports, setReports] = useState<Report[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [listFetchError, setListFetchError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  // ========== Download state ==========
  const [downloadingReportId, setDownloadingReportId] = useState<number | null>(
    null
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // ========== Generation state ==========
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const canFetch =
    isAllowed && !isLoading && isAuthenticated && Boolean(tenantId);

  // ------------------------------------------------------------------------------------
  // Loading the list
  //
  // `showLoading` separates the two reasons this runs. A foreground load (mount,
  // tenant switch) owns the page's loading and error surfaces. A background
  // refresh — the generation poll — must never blank the table or replace it
  // with an error card over a single failed tick: the rows on screen are still
  // the best answer we have.
  // ------------------------------------------------------------------------------------
  const loadReports = useCallback(
    async (showLoading: boolean) => {
      if (!canFetch) return;
      try {
        if (showLoading) {
          setIsFetching(true);
          setListFetchError(null);
        }
        const list = await fetchReportsList(api, tenantId);
        setReports(list);
      } catch (err) {
        console.error("Error fetching reports:", err);
        if (showLoading) {
          setReports([]);
          setListFetchError(
            readApiErrorMessage(err, "Failed to load reports")
          );
        }
      } finally {
        if (showLoading) {
          setIsFetching(false);
          setHasFetched(true);
        }
      }
    },
    [api, canFetch, tenantId]
  );

  useEffect(() => {
    if (!canFetch) return;
    setHasFetched(false);
    void loadReports(true);
  }, [canFetch, loadReports]);

  const refreshReports = useCallback(() => {
    void loadReports(false);
  }, [loadReports]);

  // Keeps `pending` / `generating` rows moving without a manual reload.
  const isPolling = useReportsAutoRefresh({
    enabled: canFetch,
    reports,
    onRefresh: refreshReports,
  });

  // ------------------------------------------------------------------------------------
  // Filters
  // ------------------------------------------------------------------------------------
  const reportFields = useMemo(() => makeReportFields(), []);

  const normalizedFilters = useMemo(
    () =>
      normalizeFilters<Report>(activeFilters, reportFields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, reportFields]
  );

  const predicate = useMemo(
    () => buildPredicate<Report>(normalizedFilters, reportFields),
    [normalizedFilters, reportFields]
  );

  const filteredData = useMemo(
    () => reports.filter(predicate),
    [reports, predicate]
  );

  const suggestedKeys = useMemo(
    () => reportFields.map((field) => field.label),
    [reportFields]
  );

  // ------------------------------------------------------------------------------------
  // Download
  // ------------------------------------------------------------------------------------
  const handleDownload = useCallback(
    async (report: Report) => {
      if (downloadingReportId === report.id) return; // Prevent duplicate downloads

      try {
        setDownloadingReportId(report.id);

        const { downloadUrl, filename } = await fetchReportDownload(
          api,
          report.id,
          tenantId
        );

        // Signed URL: hand it to the browser rather than buffering the PDF here.
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download =
          filename || `Report_${report.reportMonth}_${report.reportYear}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading report:", error);
        setDownloadError(
          readApiErrorMessage(
            error,
            "Failed to download report. Please try again."
          )
        );
      } finally {
        setDownloadingReportId(null);
      }
    },
    [api, downloadingReportId, tenantId]
  );

  // ------------------------------------------------------------------------------------
  // Generation
  // ------------------------------------------------------------------------------------
  const openGenerateModal = useCallback(() => {
    setGenerateError(null);
    setIsModalOpen(true);
  }, []);

  const handleGenerate = useCallback(
    async (body: GenerateReportBody) => {
      try {
        setIsGenerating(true);
        setGenerateError(null);
        await requestReportGeneration(api, body, tenantId);
        setIsModalOpen(false);
        // The API answers 202, so the row is only visible after a re-read;
        // from there the poll carries it to completed or failed.
        await loadReports(false);
      } catch (error) {
        console.error("Error generating report:", error);
        setGenerateError(
          readApiErrorMessage(error, "Failed to request the report.")
        );
      } finally {
        setIsGenerating(false);
      }
    },
    [api, loadReports, tenantId]
  );

  // ------------------------------------------------------------------------------------
  // Columns — base plus the per-row action, which closes over the download state
  // ------------------------------------------------------------------------------------
  const columns = useMemo<GenericTableColumn<Report>[]>(
    () => [
      ...reportsBaseColumns,
      {
        header: "Actions",
        accessor: "id",
        renderCell: (row: Report) => {
          // A row with no file behind it says why, instead of offering a
          // button that cannot work.
          const undownloadable = getUndownloadableReason(row);
          if (undownloadable) {
            return (
              <span
                className={
                  undownloadable.tone === "danger"
                    ? "text-xs text-semantic-text-danger"
                    : "text-xs text-semantic-text-secondary"
                }
                title={undownloadable.detail ?? undownloadable.message}
              >
                {undownloadable.message}
              </span>
            );
          }
          const isDownloading = downloadingReportId === row.id;
          return (
            <div className="flex items-center gap-2">
              <GenericButton
                variant="brand"
                label=""
                size="sm"
                isIconOnly={true}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDownload(row);
                }}
                icon={<IconDownload className="w-4 text-white fill-white" />}
                loading={isDownloading}
                disabled={isDownloading || !isReportDownloadable(row)}
              />
            </div>
          );
        },
      },
    ],
    [downloadingReportId, handleDownload]
  );

  // ------------------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------------------
  const showInitialOverlay =
    canFetch && (!hasFetched || (isFetching && reports.length === 0));
  const overlaySpinner = useProcessingOverlay(showInitialOverlay);

  const renderContent = () => {
    if (showInitialOverlay) return overlaySpinner;
    if (canFetch && listFetchError) {
      return <ErrorFetchingItems text={listFetchError} />;
    }
    // No reports yet: the empty state carries the only way to make one, so the
    // page is never a dead end for a tenant that has never generated.
    if (canFetch && reports.length === 0) {
      return (
        <EmptyItems actionLabel="Generate report" onAction={openGenerateModal} />
      );
    }

    return (
      <div className="relative p-4 overflow-hidden flex flex-col h-full">
        <div className="relative flex-1 min-h-0">
          <GenericCard
            isCollapsible={false}
            className="h-full flex flex-col"
            bodyContent={
              <div className="flex flex-col h-full min-h-0">
                <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                  <div className="flex items-center justify-between gap-4 my-5">
                    <div className="w-[520px]">
                      <GenericSearchbar
                        placeholder="Find by attribute or value"
                        suggestedKeys={suggestedKeys}
                        onFiltersChange={setActiveFilters}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      {isPolling && (
                        <span className="text-xs text-semantic-text-secondary">
                          Updating…
                        </span>
                      )}
                      <GenericButton
                        variant="brand"
                        size="md"
                        label="Generate report"
                        onClick={openGenerateModal}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-h-0 flex flex-col">
                  <GenericTable
                    columns={columns}
                    data={filteredData}
                    initialRowsPerPage={
                      filteredData.length > 0
                        ? Math.max(10, filteredData.length)
                        : 10
                    }
                    rowsPerPageOptions={
                      [
                        10,
                        25,
                        50,
                        100,
                        filteredData.length > 100
                          ? filteredData.length
                          : undefined,
                      ].filter(Boolean) as number[]
                    }
                    tableClassName="bg-[var(--semantic-bg-primary)]"
                    width="100%"
                    maxTableBodyHeight="100%"
                  />
                </div>
              </div>
            }
          />
        </div>
      </div>
    );
  };

  return (
    <RequireTenantType allowedTypes={[...ALLOWED_TENANT_TYPES]}>
      {renderContent()}

      <GenerateReportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={(body) => void handleGenerate(body)}
        isSubmitting={isGenerating}
        errorMessage={generateError}
      />

      <GenericAlert
        isOpen={Boolean(downloadError)}
        onClose={() => setDownloadError(null)}
        onConfirm={() => setDownloadError(null)}
        variant="error"
        title="Download Error"
        description={downloadError ?? ""}
        confirmLabel="OK"
        hideCancel={true}
      />
    </RequireTenantType>
  );
}
